/**
 * Compressione/ridimensionamento immagini lato client per il logo squadra.
 *
 * Il backend accetta un data-URL fino a ~256KB (stringa base64). Foto del
 * telefono o loghi generati (ChatGPT ecc.) superano facilmente il limite.
 * Invece di rifiutarli, qui li ridimensioniamo + comprimiamo finché la
 * stringa data-URL sta sotto la soglia.
 *
 * Strategia: disegna su canvas a lato max `maxDim`, poi prova qualità
 * decrescenti (WebP se supportato, altrimenti JPEG); se ancora troppo
 * grande, riduce le dimensioni e riprova. Gli SVG vengono rasterizzati
 * (perdono la vettorialità ma diventano caricabili).
 *
 * Andrea 2026-09-17: `preparaLogo` affianca all'originale la versione
 * senza sfondo (vedi rimuoviSfondo.ts), da proporre in anteprima.
 */

import { canvasSenzaSfondo } from "./rimuoviSfondo";

/** Carica un File immagine in un HTMLImageElement (object-URL, poi revoca). */
function loadImageFromFile(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("immagine non leggibile"));
    };
    img.src = url;
  });
}

/** Carica un'immagine da un data-URL (il logo gia' salvato). */
function loadImageFromSrc(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("immagine non leggibile"));
    img.src = src;
  });
}

/** Sceglie il formato di export migliore supportato dal browser. Con la
 * trasparenza da tenere (`alpha`) il ripiego e' PNG: il JPEG la perderebbe. */
function pickFormat(alpha = false): "image/webp" | "image/jpeg" | "image/png" {
  const c = document.createElement("canvas");
  c.width = 1;
  c.height = 1;
  if (c.toDataURL("image/webp").startsWith("data:image/webp")) return "image/webp";
  return alpha ? "image/png" : "image/jpeg";
}

interface CompressOpts {
  /** Limite massimo della stringa data-URL (default 250KB, margine sotto i 256KB server). */
  maxBytes?: number;
  /** Lato lungo massimo in px (default 512 — i loghi si mostrano a 48–80px). */
  maxDim?: number;
}

/**
 * Comprime un'immagine in un data-URL sotto `maxBytes`. Ritorna sempre un
 * data-URL valido; se nemmeno al minimo riesce a stare sotto soglia, ritorna
 * la versione più piccola ottenuta (il server farà l'ultima validazione).
 */
export async function compressImageToDataUrl(
  file: File,
  opts: CompressOpts = {},
): Promise<string> {
  return comprimi(await loadImageFromFile(file), opts);
}

/** Il logo pronto da caricare, in due versioni (Andrea 2026-09-17):
 * com'e' e senza lo sfondo a tinta unita. `senzaSfondo` e' null quando non
 * c'e' niente da togliere (gia' trasparente, foto, sfondo sfumato).
 * `sorgente` = il file scelto, oppure il data-URL del logo gia' salvato. */
export async function preparaLogo(
  sorgente: File | string,
  opts: CompressOpts = {},
): Promise<{ originale: string; senzaSfondo: string | null }> {
  const img =
    typeof sorgente === "string"
      ? await loadImageFromSrc(sorgente)
      : await loadImageFromFile(sorgente);
  // Il logo gia' salvato e' gia' sotto soglia: ricomprimerlo lo sgranerebbe.
  const originale = typeof sorgente === "string" ? sorgente : comprimi(img, opts);
  const ritagliato = canvasSenzaSfondo(img, opts.maxDim ?? 512);
  return {
    originale,
    senzaSfondo: ritagliato ? comprimi(ritagliato, opts, true) : null,
  };
}

function comprimi(
  img: HTMLImageElement | HTMLCanvasElement,
  opts: CompressOpts,
  alpha = false,
): string {
  const maxBytes = opts.maxBytes ?? 250 * 1024;
  const startDim = opts.maxDim ?? 512;
  const format = pickFormat(alpha);
  const larghezza = img instanceof HTMLImageElement ? img.naturalWidth || img.width : img.width;
  const altezza = img instanceof HTMLImageElement ? img.naturalHeight || img.height : img.height;

  let dim = startDim;
  let best = "";
  while (dim >= 96) {
    let w = larghezza || dim;
    let h = altezza || dim;
    if (w > dim || h > dim) {
      const scale = dim / Math.max(w, h);
      w = Math.max(1, Math.round(w * scale));
      h = Math.max(1, Math.round(h * scale));
    }

    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("canvas non disponibile");
    // JPEG non supporta trasparenza → sfondo bianco. WebP la mantiene.
    if (format === "image/jpeg") {
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, w, h);
    } else {
      ctx.clearRect(0, 0, w, h);
    }
    ctx.drawImage(img, 0, 0, w, h);

    for (let q = 0.92; q >= 0.4; q -= 0.08) {
      const url = canvas.toDataURL(format, q);
      best = url;
      if (url.length <= maxBytes) return url;
      if (format === "image/png") break; // senza perdita: la qualita' non conta
    }
    dim = Math.round(dim * 0.8);
  }
  return best;
}
